import {
  joinMembershipPath,
  membershipStatusPage,
  onboardingQuizPath,
  searchPath,
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import { usePaymentMethods } from "../../hooks/CurrentUser";
import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { useCreditOffer } from "../../hooks/useCreditOffer";
import { useMembershipRequirement } from "../subscriptions/hooks/useMembershipRequirement";
import ArtShowcaseBanner, {
  IsValidTimeRangeForArtShowcaseBanner,
} from "./ArtShowcaseBanner";
import BackupPaymentBanner from "./BackupPaymentBanner";
import CreditOfferBanner from "./CreditOfferBanner";
import FirstMonthFreeBanner from "./FirstMonthFreeBanner";
import ReferralSignupBanner from "./ReferralSignupBanner";

// Use this for running experiments and diverting user cohorts to different membership banners.
export default function MembershipsBannerSwitcher() {
  const { isLoggedIn, currentUser, currentUserIsLoading } = useSession();
  const { creditOffer, loading: creditOfferLoading } = useCreditOffer();
  const location = useLocation();
  const { shouldShowBannersToday, isBannerDismissed } = useBannerDismissal();

  const { mustPurchaseMembership } = useMembershipRequirement();
  const { loading: paymentMethodsLoading } = usePaymentMethods();

  // Global rules: Don't show any banners on these pages
  const shouldShowBannerByPath = React.useMemo(() => {
    if (
      location.pathname.includes(onboardingQuizPath()) ||
      location.pathname.includes(searchPath()) ||
      location.pathname.includes("/users/") ||
      location.pathname.includes("/membership/subscribe/") ||
      location.pathname.includes(membershipStatusPage()) ||
      location.pathname === joinMembershipPath() ||
      location.pathname.includes("/teach/") ||
      location.pathname.includes("/account/teacher") ||
      location.pathname.includes("/class-comparison")
    ) {
      return false;
    }

    return true;
  }, [location.pathname]);

  if (currentUserIsLoading || creditOfferLoading || paymentMethodsLoading) {
    return null;
  }

  const firstMonthFreeUsers =
    !isLoggedIn || (mustPurchaseMembership && !currentUser?.hasEverSubscribed);

  // Make a special exception for the manual coupon experiment
  // Please remove this block when the experiment is complete
  if (
    location.pathname.includes("/membership/subscribe/") &&
    firstMonthFreeUsers
  ) {
    return <FirstMonthFreeBanner />;
  }
  const musicShowcaseDismissed = isBannerDismissed("music_showcase");
  const shouldShowBackupBanner = isLoggedIn && !paymentMethodsLoading;

  // If a banner was dismissed today, don't show any banners
  if (!shouldShowBannersToday || !shouldShowBannerByPath) {
    return null;
  }

  const wasAccountCreatedAfterShowcaseBanner =
    currentUser && dayjs("2026-03-09").isBefore(currentUser?.created_at);

  if (
    !musicShowcaseDismissed &&
    IsValidTimeRangeForArtShowcaseBanner() &&
    isLoggedIn &&
    !wasAccountCreatedAfterShowcaseBanner
  ) {
    return <ArtShowcaseBanner />;
  } else if (isLoggedIn && creditOffer) {
    return <CreditOfferBanner />;
  } else if (firstMonthFreeUsers) {
    return <FirstMonthFreeBanner />;
  } else if (shouldShowBackupBanner) {
    return <BackupPaymentBanner />;
  } else if (isLoggedIn) {
    return <ReferralSignupBanner />;
  } else {
    return null;
  }
}
